import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import type { SectionSetting } from '@/types/database.types'

/* ============================================================
   useSectionSettings — PRD §8.1
   Section ordering and visibility management.
   ============================================================ */

/** Get all section settings ordered by display_order */
export function useSectionSettings(tenantId: string | undefined) {
  return useQuery({
    queryKey: ['section-settings', tenantId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('section_settings')
        .select('*')
        .eq('tenant_id', tenantId!)
        .order('display_order', { ascending: true })

      if (error) throw error
      return (data ?? []) as SectionSetting[]
    },
    enabled: !!tenantId,
    staleTime: 5 * 60 * 1000, // 5 min
  })
}

/** Get only enabled sections (for consumer site rendering) */
export function useEnabledSections(tenantId: string | undefined) {
  return useQuery({
    queryKey: ['section-settings', tenantId, 'enabled'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('section_settings')
        .select('*')
        .eq('tenant_id', tenantId!)
        .eq('enabled', true)
        .order('display_order', { ascending: true })

      if (error) throw error
      return (data ?? []) as SectionSetting[]
    },
    enabled: !!tenantId,
    staleTime: 5 * 60 * 1000,
  })
}

/** Toggle section visibility */
export function useToggleSection() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, enabled }: { id: string; enabled: boolean }) => {
      const { error } = await supabase.from('section_settings')
        .update({ enabled } as never)
        .eq('id', id)
      if (error) throw error
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['section-settings'] })
    },
  })
}

/** Reorder sections (batch update display_order) */
export function useReorderSections() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (
      updates: Array<{ id: string; display_order: number }>
    ) => {
      // Update each section's display_order
      const promises = updates.map(({ id, display_order }) =>
        supabase.from('section_settings')
          .update({ display_order } as never)
          .eq('id', id)
      )
      const results = await Promise.all(promises)
      const failed = results.find((r: { error?: unknown }) => r.error)
      if (failed?.error) throw failed.error
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['section-settings'] })
    },
  })
}
